import { z } from '../zod';
import { DashboardUrlField } from '../shared/dashboardUrl';
import { PagePaymentMethodsInputValidationSchema } from '../page/common/paymentMethods';
import { PagePaymentOptionResponseValidationSchema } from '../page/common/paymentOptions';
import { PageFeeResponseValidationSchema } from '../page/common/fee';
import { PageInvoiceSettingsInputValidationSchema } from '../page/common/invoiceSettings';
import { PageCoverProcessingFeesResponseValidationSchema } from '../page/common/coverProcessingFees';
import { CheckoutAbandonmentResponseValidationSchema } from '../page/common/abandonmentSettings';
import { ProductResponseValidationSchema } from '../product/response';
import { SharedPageResponseValidationSchema } from '../page/get';
import { ThemeOverridesToolResponseValidationSchema } from '../theme/themeOverrides';

export const CheckoutPageResponseSchema = SharedPageResponseValidationSchema.extend({
  type: z.literal('checkout').meta({
    description: 'Type of page.',
  }),
  showCouponCodeField: z.boolean().nullable().optional().meta({
    description: 'Whether to show the coupon code field.',
  }),
  showCouponCodeFieldType: z.enum(['link', 'field']).nullable().optional().meta({
    description: 'Display type for coupon code field.',
  }),
  allowDynamicPrice: z.boolean().nullable().optional().meta({
    description: 'Whether the price can be set dynamically via URL parameters.',
  }),
  allowDynamicDiscountedFromPrice: z.boolean().nullable().optional().meta({
    description: 'Whether the discounted from price can be set dynamically via URL parameters.',
  }),
  allowDynamicTitle: z.boolean().nullable().optional().meta({
    description: 'Whether the title can be set dynamically via URL parameters.',
  }),
  allowDynamicDescription: z.boolean().nullable().optional().meta({
    description: 'Whether the description can be set dynamically via URL parameters.',
  }),
  allowDynamicLabel: z.boolean().nullable().optional().meta({
    description: "Whether a price's label can be set dynamically via URL parameters.",
  }),
  allowDynamicPreselectedPrice: z.boolean().nullable().optional().meta({
    description: 'Whether a price can be preselected (and revealed if hidden) via URL parameters.',
  }),
  allowDynamicPlanIterations: z.boolean().nullable().optional().meta({
    description: 'Whether subscription plan iterations can be set dynamically via URL parameters.',
  }),
  allowDynamicRedirectUrl: z.boolean().nullable().optional().meta({
    description: 'Whether the redirect URL can be set dynamically via URL parameters.',
  }),
  savePaymentMethod: z.boolean().nullable().optional().meta({
    description:
      'Whether to save payment method on file for one-time payments. Payment methods for subscriptions are always saved.',
  }),
  checkoutAbandonment: CheckoutAbandonmentResponseValidationSchema.optional().meta({
    description: 'Checkout abandonment email reminder settings.',
  }),
  enableFileAccessForInactiveSubscriptions: z.boolean().nullish().meta({
    description:
      "Deprecated — superseded by the product's `enableFileAccessForInactiveSubscriptions`. Mirrors the product value.",
    deprecated: true,
  }),
  limitSubscriptions: z
    .object({
      enabled: z.boolean().nullish(),
    })
    .nullish()
    .meta({
      description:
        "Deprecated — superseded by the product's `limitSubscriptions.enabled`. Mirrors the product value.",
      deprecated: true,
    }),
  limitSubscriptionsStripe: z
    .object({
      enabled: z.boolean().nullish(),
    })
    .nullish()
    .meta({
      description:
        "Deprecated — superseded by the product's `limitSubscriptions.limitSubscriptionsStripe.enabled`. Mirrors the product value.",
      deprecated: true,
    }),
  sendCanceledSubscriptionNotifications: z.boolean().nullish().meta({
    description:
      'Whether to send notifications to the store owner when a subscription is canceled.',
  }),
  sendRecurringPaymentFailedNotifications: z.boolean().nullish().meta({
    description: 'Whether to send notifications to the store owner when a recurring payment fails.',
  }),
  sendRecurringPaymentSucceededNotifications: z.boolean().nullish().meta({
    description:
      'Whether to send notifications to the store owner when a recurring payment succeeds.',
  }),
  tax: z
    .object({
      enabled: z.boolean().nullish().meta({
        description: 'Whether tax collection is enabled.',
      }),
      mode: z.enum(['none', 'fixed', 'stripe']).nullish().meta({
        description:
          'Tax collection mode. `stripe` uses Stripe Tax, `fixed` applies a fixed rate (rate IDs live on `product.fixedTaxRateIds`), `none` collects no tax.',
      }),
    })
    .nullish()
    .meta({
      description: 'Tax configuration for this page.',
    }),
  paymentMethods: PagePaymentMethodsInputValidationSchema.nullish().meta({
    description: 'Payment method configuration for this page.',
  }),
  paymentOptions: z.array(PagePaymentOptionResponseValidationSchema).nullish().meta({
    description: 'Payment options presented to customers at checkout.',
  }),
  fees: z.array(PageFeeResponseValidationSchema).nullish().meta({
    description: 'Extra fees applied at checkout.',
  }),
  coverProcessingFees: PageCoverProcessingFeesResponseValidationSchema,
  invoiceSettings: PageInvoiceSettingsInputValidationSchema.nullish().meta({
    description: 'Invoice configuration for manual payment flows.',
  }),
  product: ProductResponseValidationSchema.nullish(),
}).meta({ id: 'CheckoutPageResponse' });

// Only MCP reads these API payloads. It checks themeOverrides loosely, so a theme property the API starts
// exposing before the MCP worker redeploys can't fail its tools.
export const CheckoutPageMcpPayloadSchema = CheckoutPageResponseSchema.extend({
  themeOverrides: ThemeOverridesToolResponseValidationSchema,
});

export const CreateCheckoutPageResponseValidationSchema = z.object({
  data: CheckoutPageMcpPayloadSchema,
});

export const UpdateCheckoutPageResponseValidationSchema =
  CreateCheckoutPageResponseValidationSchema;

export const CheckoutPageToolDataValidationSchema = CheckoutPageMcpPayloadSchema.extend({
  hostedUrl: z.url().meta({
    description: 'The live URL where the checkout page can be accessed.',
  }),
  dashboardUrl: DashboardUrlField,
});

export const CheckoutPageToolResponseValidationSchema = z.object({
  data: CheckoutPageToolDataValidationSchema,
});

export type CheckoutPageResponse = z.infer<typeof CheckoutPageResponseSchema>;
export type CreateCheckoutPageResponse = z.infer<typeof CreateCheckoutPageResponseValidationSchema>;
export type CheckoutPageToolData = z.infer<typeof CheckoutPageToolDataValidationSchema>;
export type CheckoutPageToolResponse = z.infer<typeof CheckoutPageToolResponseValidationSchema>;
